/**
 * Build-time configuration. Vite inlines `import.meta.env.VITE_*` into the
 * bundle, so nothing here is a secret and nothing here can change at runtime —
 * a different environment means a different build (or a different nginx proxy
 * target, which is the seam we actually use).
 */

function trimSlashes(value: string): string {
  return value.replace(/^\/+|\/+$/g, '');
}

/** Must match the backend's `API_PREFIX`. */
export const API_PREFIX = `/${trimSlashes(import.meta.env.VITE_API_PREFIX || 'v1')}`;

/**
 * Empty by default, which means "same origin as the page". That is the happy
 * path: the dev server and nginx both proxy `${API_PREFIX}/*` to the backend,
 * so the browser sees a single origin and the httpOnly auth cookies need no
 * CORS and no SameSite exemption.
 */
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');

export const APP_NAME = import.meta.env.VITE_APP_NAME || 'Epic Hub';

/**
 * The zone every timestamp is *displayed* in. Storage and transport are UTC
 * throughout — Postgres `timestamptz`, ISO-8601 on the wire — and this is the
 * single place where an instant becomes a wall clock.
 *
 * Pinned to the shop's own zone rather than left to the browser, because this
 * is one shop in one building: "3:40pm" on a job has to mean the same thing to
 * everyone reading it, whether their laptop is set to Phoenix, to the zone they
 * are travelling in, or to nothing in particular. A distributed product would
 * want the opposite default.
 *
 * Arizona does not observe DST, so America/Phoenix is permanently MST (UTC-7)
 * and this offset never moves under us. The IANA name is still what is stored
 * here rather than a fixed offset, so that a future move to a DST-observing
 * zone is a config change and not a bug hunt.
 */
export const APP_TIME_ZONE = import.meta.env.VITE_APP_TIME_ZONE || 'America/Phoenix';

/**
 * Whether to offer self-service signup. Cosmetic only — the API is what
 * actually decides, and refuses with a 403 when its own REGISTRATION_ENABLED is
 * off. This exists so a deployment with signup closed does not show the user a
 * form that cannot succeed. Keep the two in step.
 */
export const REGISTRATION_ENABLED = import.meta.env.VITE_REGISTRATION_ENABLED !== 'false';

/** `/users/me` → `/v1/users/me` (optionally absolute). */
export function apiUrl(path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${API_PREFIX}${suffix}`;
}

/**
 * Where browser crashes are reported, if anywhere. Absent — the default
 * everywhere but the droplet — error reporting is inert. See lib/monitoring.ts.
 */
export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';
export const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
/** The deployed revision, so an issue says which build it came from. */
export const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE || undefined;
